import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, Mail } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 10;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('polling'); // polling | paid | failed | error
  const [details, setDetails] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const fetchStatus = useCallback(async () => {
    if (!sessionId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/payments/status/${sessionId}`);
      setDetails(data);
      if (data.payment_status === 'paid') {
        setStatus('paid');
      } else if (['failed', 'expired', 'refunded'].includes(data.payment_status)) {
        setStatus('failed');
      }
    } catch (err) {
      setStatus('error');
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    fetchStatus();
  }, [sessionId, fetchStatus]);

  useEffect(() => {
    if (status !== 'polling') return;
    if (pollCount >= MAX_POLLS) return;
    const t = setTimeout(() => {
      setPollCount((c) => c + 1);
      fetchStatus();
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [status, pollCount, fetchStatus]);

  const formatAmount = (amount, currency) => {
    if (!amount) return '';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: (currency || 'eur').toUpperCase(),
    }).format(amount / 100);
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b] text-[#dfddd6]">
      <Header />
      <main className="max-w-2xl mx-auto px-5 md:px-10 py-24 md:py-32">
        <div className="bg-[#302f2c] border border-[#3f4816] rounded-2xl p-8 md:p-12 text-center">
          {status === 'polling' && (
            <>
              <Loader2 className="mx-auto text-[#d9fb06] animate-spin mb-6" size={64} data-testid="payment-loading-icon" />
              <h1 className="text-3xl md:text-4xl font-black text-[#d9fb06] uppercase mb-4">
                Vérification en cours…
              </h1>
              <p className="text-[#888680] mb-2">
                Nous confirmons votre paiement auprès de Stripe.
              </p>
              <p className="text-[#888680] text-sm">Merci de patienter quelques secondes.</p>
            </>
          )}

          {status === 'paid' && (
            <>
              <CheckCircle2 className="mx-auto text-[#d9fb06] mb-6" size={72} data-testid="payment-success-icon" />
              <h1 className="text-3xl md:text-4xl font-black text-[#d9fb06] uppercase mb-4" data-testid="payment-success-title">
                Paiement confirmé !
              </h1>
              <p className="text-[#dfddd6] text-lg mb-2">
                Merci pour votre confiance. Votre commande est bien reçue.
              </p>
              {details?.amount ? (
                <p className="text-[#888680] mb-6" data-testid="payment-amount">
                  Montant : <span className="text-[#d9fb06] font-bold">{formatAmount(details.amount, details.currency)}</span>
                </p>
              ) : null}
              <div className="bg-[#1a1c1b] border border-[#3f4816] rounded-lg p-4 mb-6 text-left inline-block">
                <div className="flex items-center space-x-3">
                  <Mail className="text-[#d9fb06] flex-shrink-0" size={20} />
                  <p className="text-sm text-[#888680]">
                    Un reçu vous sera envoyé par email. <br />
                    Je vous recontacte sous <span className="text-[#d9fb06] font-bold">24h ouvrées</span> pour démarrer la prestation.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/">
                  <Button
                    data-testid="payment-success-home-btn"
                    className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-8 py-3 text-sm font-bold uppercase"
                  >
                    Retour à l'accueil
                  </Button>
                </Link>
              </div>
            </>
          )}

          {(status === 'failed' || (status === 'polling' && pollCount >= MAX_POLLS)) && (
            <>
              <XCircle className="mx-auto text-red-400 mb-6" size={64} />
              <h1 className="text-3xl md:text-4xl font-black text-red-400 uppercase mb-4">
                Paiement non confirmé
              </h1>
              <p className="text-[#888680] mb-6">
                {status === 'failed'
                  ? "Votre paiement a échoué ou a été annulé."
                  : "Nous n'avons pas pu confirmer le paiement dans le délai imparti."}
                <br />
                Aucun montant n'a été prélevé, ou vous serez remboursé automatiquement.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/">
                  <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-8 py-3 text-sm font-bold uppercase">
                    Retour à l'accueil
                  </Button>
                </Link>
                <Link to="/#pricing">
                  <Button variant="outline" className="rounded-full px-8 py-3 text-sm font-bold uppercase border-[#d9fb06] text-[#d9fb06] hover:bg-[#d9fb06] hover:text-[#1a1c1b]">
                    Réessayer
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="mx-auto text-red-400 mb-6" size={64} />
              <h1 className="text-3xl md:text-4xl font-black text-red-400 uppercase mb-4">
                Erreur
              </h1>
              <p className="text-[#888680] mb-6">
                Impossible de récupérer le statut du paiement. Merci de nous contacter si un montant a été prélevé.
              </p>
              <Link to="/">
                <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-8 py-3 text-sm font-bold uppercase">
                  Retour à l'accueil
                </Button>
              </Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
